import { Graph3D, type Graph3DHandle } from '@packages/graph-3d';
import React, { useEffect, useMemo, useRef } from 'react';
import { useConfig } from '../../../../hooks/useConfig';
import type { ProcessRuntimeState, TaskPhase } from './types';
import { resolvePersonAvatarSources } from './avatarDemo';
import {
  buildProcessGraph3DData,
  type ProcessGraph3DNodeData,
} from './adapters/buildProcessGraph3DData';
import { buildProcessRuntimeView } from './adapters/buildProcessRuntimeView';
import {
  compileProcessGraph,
  type ProcessGraphCompileResult,
  type RawProcessNode,
} from './utils/processGraph';

const FINAL_VIEWPORT_PADDING = 56;
const FINAL_VIEWPORT_ANIMATION_MS = 900;

interface ProcessDiagram3DProps {
  processJson: string;
  phase: TaskPhase;
  processStates: Map<string, ProcessRuntimeState>;
  suppressMotion?: boolean;
  onCompiled?: (graph: ProcessGraphCompileResult) => void;
  onDoneViewportSettled?: () => void;
}

const ProcessDiagram3D: React.FC<ProcessDiagram3DProps> = ({
  processJson,
  phase,
  processStates,
  suppressMotion = false,
  onCompiled,
  onDoneViewportSettled,
}) => {
  const { globalConfig } = useConfig();
  const enablePersonAvatarDemo = globalConfig.enablePersonAvatarDemo !== false;
  const graphRef = useRef<Graph3DHandle | null>(null);
  const waitingDoneViewportRef = useRef(false);
  const finalFitTriggeredRef = useRef(false);
  const includeResults = phase === 'running' || phase === 'done';

  const compiled = useMemo<ProcessGraphCompileResult | null>(() => {
    if (!processJson) {
      return null;
    }

    try {
      const parsed = JSON.parse(processJson) as unknown;
      const root = unwrapProcessRoot(parsed);
      if (!root) {
        return null;
      }
      return compileProcessGraph(root);
    } catch (error) {
      console.error('[ProcessDiagram3D] processJson parse failed', error);
      return null;
    }
  }, [processJson]);

  useEffect(() => {
    if (!compiled || !onCompiled) {
      return;
    }
    onCompiled(compiled);
  }, [compiled, onCompiled]);

  const roleAvatarByRoleId = useMemo(() => {
    const next = new Map<
      string,
      { avatarUrl?: string; avatarFallbackUrls: string[] }
    >();
    if (!compiled) {
      return next;
    }

    compiled.processToRoleIds.forEach((roleIds, processId) => {
      const state = processStates.get(processId);
      if (!state) {
        return;
      }

      roleIds.forEach((roleId, index) => {
        const slot = state.slots[index];
        const person = slot?.person;
        if (!person) {
          return;
        }
        const avatarSources = resolvePersonAvatarSources(
          person.name,
          person.avatarUrl,
          enablePersonAvatarDemo
        );
        if (
          avatarSources.avatarUrl ||
          avatarSources.avatarFallbackUrls.length > 0
        ) {
          next.set(roleId, avatarSources);
        }
      });
    });

    return next;
  }, [compiled, enablePersonAvatarDemo, processStates]);

  const data = useMemo(() => {
    if (!compiled) {
      return null;
    }

    const base = buildProcessGraph3DData(compiled, { includeResults });
    return {
      ...base,
      nodes: base.nodes.map(node => {
        if (node.type !== 'role') {
          return node;
        }
        const roleData = node.data as Extract<
          ProcessGraph3DNodeData,
          { kind: 'role' }
        >;
        const avatarSources = roleAvatarByRoleId.get(node.id);
        return {
          ...node,
          data: {
            ...roleData,
            avatarUrl: avatarSources?.avatarUrl,
            avatarFallbackUrls: avatarSources?.avatarFallbackUrls || [],
          },
        };
      }),
    };
  }, [compiled, includeResults, roleAvatarByRoleId]);

  const runtime = useMemo(() => {
    if (!compiled) {
      return undefined;
    }
    return buildProcessRuntimeView(compiled, processStates, { includeResults });
  }, [compiled, includeResults, processStates]);

  useEffect(() => {
    if (phase !== 'done') {
      waitingDoneViewportRef.current = false;
      finalFitTriggeredRef.current = false;
    }
  }, [phase]);

  useEffect(() => {
    if (!compiled || phase !== 'done') {
      return;
    }

    if (suppressMotion) {
      if (!finalFitTriggeredRef.current) {
        finalFitTriggeredRef.current = true;
        onDoneViewportSettled?.();
      }
      return;
    }

    if (finalFitTriggeredRef.current || !graphRef.current) {
      return;
    }

    finalFitTriggeredRef.current = true;
    waitingDoneViewportRef.current = true;
    graphRef.current.fitView({
      padding: FINAL_VIEWPORT_PADDING,
      duration: FINAL_VIEWPORT_ANIMATION_MS,
      includeHiddenNodes: true,
    });
  }, [compiled, onDoneViewportSettled, phase, suppressMotion]);

  if (!data) {
    return <div className="h-full w-full bg-white" />;
  }

  return (
    <div className="h-full w-full bg-white">
      <Graph3D
        ref={graphRef}
        data={data}
        runtime={runtime}
        layout={{ type: 'preset', center: true, zGap: 18 }}
        interactions={{
          enableAutoFit: true,
          runtimeViewportFollow: {
            enabled: !suppressMotion && phase === 'running',
            nodeTypes: ['process'],
            statuses: ['running'],
            padding: 0.62,
            duration: 320,
          },
          enableHoverDetailPanel: true,
        }}
        style={{ width: '100%', height: '100%' }}
        events={{
          onViewportSettled: () => {
            if (!waitingDoneViewportRef.current) {
              return;
            }
            waitingDoneViewportRef.current = false;
            onDoneViewportSettled?.();
          },
        }}
      />
    </div>
  );
};

function unwrapProcessRoot(value: unknown): RawProcessNode | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as {
    process?: RawProcessNode;
    name?: string;
  };

  if (candidate.process && typeof candidate.process === 'object') {
    return candidate.process;
  }

  if (typeof candidate.name === 'string') {
    return candidate as RawProcessNode;
  }

  return null;
}

export default ProcessDiagram3D;
